import React, { useEffect, useRef, useState } from 'react';
import { Play, Pause, Volume2, VolumeX, Maximize2 } from 'lucide-react';

/**
 * EnterpriseVideo
 * ---------------------------------------------------------------------------
 * Shared video component for the Enterprise Solutions page and all of its
 * subpages (every industry vertical and the Logistics function subpage).
 *
 * Features (identical for every video on this surface):
 *   - Three bottom-right controls: play/pause, mute/unmute, fullscreen.
 *     Styled to match the "Enterprise AI In Action" reference on the AI
 *     Employee page: small dark rounded-square buttons grouped together.
 *   - Discreet behavior: controls are invisible until the user hovers on
 *     desktop, and appear faintly on touch devices then fade out after a
 *     short period of inactivity.
 *   - Autoplay when the video is at least ~50% visible on screen; pauses
 *     automatically when it scrolls out of view.
 *   - Starts muted and loops (unchanged). Audio only plays after the user
 *     taps the volume button.
 *   - Two source modes:
 *       1) Cloudflare Stream iframe (pass `cloudflareVideoId`), controlled
 *          via the Cloudflare Stream Player SDK.
 *       2) Direct <video> element (pass `videoSrc`), controlled via the
 *          DOM media element.
 *
 * The outer wrapper is `relative overflow-hidden` by default; callers pass
 * in their existing frame styling (border, rounded corners, gold frame,
 * aspect ratio, box-shadow) through `className` and `style`, so this
 * component never changes any surrounding layout or visuals.
 */

const CLOUDFLARE_CUSTOMER = 'customer-q87c1i5e2o40bvjt';

const EnterpriseVideo = ({
  cloudflareVideoId,
  videoSrc,
  poster,
  posterTime,
  title = 'Video',
  testId,
  className = '',
  style,
}) => {
  const wrapperRef = useRef(null);
  const iframeRef = useRef(null);
  const videoRef = useRef(null);
  const streamPlayerRef = useRef(null);
  const inViewRef = useRef(true);
  const touchTimeoutRef = useRef(null);

  const [isPlaying, setIsPlaying] = useState(true);
  const [isMuted, setIsMuted] = useState(true);
  const [isHovered, setIsHovered] = useState(false);
  const [touchActive, setTouchActive] = useState(false);
  const [sdkReady, setSdkReady] = useState(false);

  const useIframe = Boolean(cloudflareVideoId);

  const posterUrl = cloudflareVideoId
    ? `https://${CLOUDFLARE_CUSTOMER}.cloudflarestream.com/${cloudflareVideoId}/thumbnails/thumbnail.jpg?time=${posterTime ?? 0}s&height=1080`
    : poster;

  // Wire the Cloudflare Stream Player SDK to the iframe (iframe mode only).
  useEffect(() => {
    if (!useIframe) return undefined;

    const initPlayer = () => {
      if (iframeRef.current && window.Stream) {
        streamPlayerRef.current = window.Stream(iframeRef.current);
        setSdkReady(true);
      }
    };

    if (window.Stream) {
      initPlayer();
      return undefined;
    }

    let script = document.querySelector('script[data-cloudflare-stream-sdk]');
    if (!script) {
      script = document.createElement('script');
      script.src = 'https://embed.cloudflarestream.com/embed/sdk.latest.js';
      script.setAttribute('data-cloudflare-stream-sdk', 'true');
      document.body.appendChild(script);
    }
    script.addEventListener('load', initPlayer);
    return () => script.removeEventListener('load', initPlayer);
  }, [useIframe, cloudflareVideoId]);

  // Autoplay when ≥50% visible, pause when below that threshold.
  useEffect(() => {
    const el = wrapperRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') return undefined;

    const observer = new IntersectionObserver(
      ([entry]) => {
        const inView = entry.isIntersecting && entry.intersectionRatio >= 0.5;
        inViewRef.current = inView;

        if (useIframe) {
          const player = streamPlayerRef.current;
          if (!player) return;
          if (inView) {
            player.play().catch(() => {});
            setIsPlaying(true);
          } else {
            player.pause();
            setIsPlaying(false);
          }
        } else {
          const video = videoRef.current;
          if (!video) return;
          if (inView) {
            video
              .play()
              .then(() => setIsPlaying(true))
              .catch(() => {});
          } else {
            video.pause();
            setIsPlaying(false);
          }
        }
      },
      { threshold: [0, 0.5, 1] }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [useIframe]);

  // When the Cloudflare SDK becomes ready, sync playback with current visibility.
  useEffect(() => {
    if (!useIframe || !sdkReady) return;
    const player = streamPlayerRef.current;
    if (!player) return;
    if (inViewRef.current) {
      player.play().catch(() => {});
      setIsPlaying(true);
    } else {
      player.pause();
      setIsPlaying(false);
    }
  }, [useIframe, sdkReady]);

  // Clean up any pending touch-hide timers on unmount.
  useEffect(
    () => () => {
      if (touchTimeoutRef.current) clearTimeout(touchTimeoutRef.current);
    },
    []
  );

  const showControlsOnTouch = () => {
    setTouchActive(true);
    if (touchTimeoutRef.current) clearTimeout(touchTimeoutRef.current);
    touchTimeoutRef.current = setTimeout(() => setTouchActive(false), 2500);
  };

  const togglePlayPause = () => {
    if (useIframe) {
      const player = streamPlayerRef.current;
      if (!player) return;
      if (isPlaying) {
        player.pause();
      } else {
        player.play().catch(() => {});
      }
      setIsPlaying((p) => !p);
    } else {
      const video = videoRef.current;
      if (!video) return;
      if (isPlaying) {
        video.pause();
      } else {
        video.play().catch(() => {});
      }
      setIsPlaying((p) => !p);
    }
  };

  const toggleMute = () => {
    const next = !isMuted;
    if (useIframe) {
      const player = streamPlayerRef.current;
      if (!player) return;
      try {
        player.muted = next;
      } catch (_) {
        // no-op — SDK not yet ready
      }
    } else {
      const video = videoRef.current;
      if (!video) return;
      video.muted = next;
    }
    setIsMuted(next);
  };

  const enterFullscreen = () => {
    const el = wrapperRef.current;
    if (!el) return;
    const req =
      el.requestFullscreen ||
      el.webkitRequestFullscreen ||
      el.msRequestFullscreen;
    if (req) req.call(el);
  };

  // Discreet visibility: full on hover (desktop), faint on touch, else hidden.
  const controlsOpacity = isHovered ? 1 : touchActive ? 0.6 : 0;

  const controlBtn =
    'w-9 h-9 rounded-md bg-black/60 backdrop-blur-sm hover:bg-black/80 flex items-center justify-center transition-colors duration-200';
  const controlIcon = 'w-4 h-4 text-white';

  return (
    <div
      ref={wrapperRef}
      className={`relative overflow-hidden ${className}`.trim()}
      style={style}
      data-testid={testId}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onTouchStart={showControlsOnTouch}
    >
      {useIframe ? (
        <iframe
          ref={iframeRef}
          title={title}
          src={`https://${CLOUDFLARE_CUSTOMER}.cloudflarestream.com/${cloudflareVideoId}/iframe?muted=true&preload=true&loop=true&autoplay=true&poster=${encodeURIComponent(
            posterUrl || ''
          )}&controls=false`}
          className="absolute inset-0 w-full h-full"
          style={{ border: 'none' }}
          allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture;"
          allowFullScreen
          data-testid={testId ? `${testId}-iframe` : undefined}
        />
      ) : (
        <video
          ref={videoRef}
          poster={poster}
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          className="absolute inset-0 w-full h-full object-cover"
          data-testid={testId ? `${testId}-video` : undefined}
        >
          <source src={videoSrc} type="video/mp4" />
        </video>
      )}

      <div
        className="absolute bottom-3 right-3 sm:bottom-4 sm:right-4 z-20 flex items-center gap-2 transition-opacity duration-200"
        style={{
          opacity: controlsOpacity,
          pointerEvents: controlsOpacity === 0 ? 'none' : 'auto',
        }}
        data-testid={testId ? `${testId}-controls` : undefined}
      >
        <button
          type="button"
          onClick={togglePlayPause}
          aria-label={isPlaying ? 'Pause video' : 'Play video'}
          className={controlBtn}
          data-testid={testId ? `${testId}-play-button` : undefined}
        >
          {isPlaying ? (
            <Pause className={controlIcon} />
          ) : (
            <Play className={controlIcon} />
          )}
        </button>
        <button
          type="button"
          onClick={toggleMute}
          aria-label={isMuted ? 'Unmute video' : 'Mute video'}
          className={controlBtn}
          data-testid={testId ? `${testId}-mute-button` : undefined}
        >
          {isMuted ? (
            <VolumeX className={controlIcon} />
          ) : (
            <Volume2 className={controlIcon} />
          )}
        </button>
        <button
          type="button"
          onClick={enterFullscreen}
          aria-label="Enter fullscreen"
          className={controlBtn}
          data-testid={testId ? `${testId}-fullscreen-button` : undefined}
        >
          <Maximize2 className={controlIcon} />
        </button>
      </div>
    </div>
  );
};

export default EnterpriseVideo;
